/* eslint-disable react/no-children-prop */
/* eslint-disable react/no-unused-prop-types */
import { Container, Flex, Heading, Text } from "@chakra-ui/react";
import ChakraUIRenderer from "chakra-ui-markdown-renderer";
import { NextSeo } from "next-seo";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

import style from "../../styles/markdown.module.css";
import IntercomHelp from "../IntercomHelp";
import Banner from "lib/components/home/Banner";

import NewsHeader from "./NewsHeader";

// Hoisted to module scope — avoids recreating the renderer object on every render.
// The custom h1→h2 remap is a plain function reference, safe to define once.
const newsMarkdownComponents = ChakraUIRenderer({
  // Remap markdown # (h1) to render as h2 — the article title
  // is already an h1 in the page template, so section headings
  // in the markdown body must be h2 for correct heading hierarchy.
  // eslint-disable-next-line react/no-unstable-nested-components
  h1: ({ children, ...props }) => (
    <Heading as="h2" size="xl" {...props}>
      {children}
    </Heading>
  ),
});

export interface UserData {
  first_name: string;
  last_name: string;
  title: string;
  avatar: string;
}

export interface NewsPostProps {
  slug: string;
  title: string;
  description: string;
  content: string;
  date_created: string;
  date_updated: string;
  media: string;
  user_created: UserData;
  user_updated: UserData;
  category: string;
}

export default function News({
  slug,
  title,
  description,
  content,
  date_created,
  date_updated,
  media,
  user_created,
  category,
}: NewsPostProps) {
  const BASE_URL = "https://adionaalert.com";
  const canonicalUrl = `${BASE_URL}/posts/${slug}`;
  const directusUrl = process.env.NEXT_PUBLIC_DIRECTUS_URL;
  const ogImage = media
    ? `${directusUrl}/assets/${media}`
    : `${BASE_URL}/og.jpg`;
  const updatedDate = new Date(date_updated);
  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title,
          description,
          images: [{ url: ogImage, alt: title }],
          type: "article",
        }}
      />
      <Banner />
      <NewsHeader
        media={media}
        title={title}
        author={user_created}
        created={date_created}
        // updated={date_updated}
        category={category}
      />
      <Container maxW="5xl">
        <Flex
          align="center"
          justify="flex-start"
          wrap="wrap"
          p={4}
          m={1}
          direction="column"
        >
          <ReactMarkdown
            components={newsMarkdownComponents}
            className={style.reactMarkDown}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw] as never}
            linkTarget="_blank"
            children={content}
            skipHtml
          />
          {date_updated !== date_created && (
            <Flex
              w="100%"
              align="left"
              justify="flex-start"
              wrap="wrap"
              direction="column"
              pt={6}
            >
              <Text fontSize="xs">
                Updated:{" "}
                {new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }).format(updatedDate)}
              </Text>
            </Flex>
          )}
        </Flex>
      </Container>
      <IntercomHelp />
    </>
  );
}
